// src/pages/admin/AdminReportsPage.jsx
// Reports & Insights landing page - standardized header with logo

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackPageView, trackReportViewed } from '../../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.invenire.app';

export default function AdminReportsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Invenire - Reports & Analytics';
    trackPageView('/admin/reports', 'Reports & Analytics');
    trackReportViewed('summary', null);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE}/scoring/summary`);
      
      if (!response.ok) {
        throw new Error('Failed to load reports summary');
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error("Failed to load data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h2>Failed to Load Reports</h2>
          <p style={{ color: '#ef4444' }}>{error}</p>
          <button onClick={loadData} style={styles.retryButton}>Retry</button>
        </div>
      </div>
    );
  }

  const jobs = data?.jobs || [];
  const summary = data?.summary || {};

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img 
            src="/invenire-logo-transparent.png" 
            alt="Invenire" 
            style={styles.logoImage}
          />
          <span style={styles.subtitle}>Reports & Analytics</span>
        </div>
        <button onClick={() => navigate('/admin')} style={styles.backButton}>
          ← Admin Home
        </button>
      </div>

      <div style={styles.content}>
        {/* Report Types */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Report Types</h2>
          <div style={styles.reportTypesGrid}>
            <div style={styles.reportTypeCard}>
              <div style={styles.reportTypeIcon}>📊</div>
              <h3 style={styles.reportTypeTitle}>Job Reports</h3>
              <p style={styles.reportTypeText}>
                View all candidates for a specific job, ranked by AI score with recommendations.
              </p>
              <span style={styles.reportTypeAction}>Select a job below →</span>
            </div>
            
            <div style={styles.reportTypeCard}>
              <div style={styles.reportTypeIcon}>👤</div>
              <h3 style={styles.reportTypeTitle}>Candidate Reports</h3>
              <p style={styles.reportTypeText}>
                Detailed breakdown of individual interviews with Q&A analysis and video timestamps.
              </p>
              <span style={styles.reportTypeAction}>Click on a candidate in job report →</span>
            </div>
            
            <div style={{ ...styles.reportTypeCard, opacity: 0.6 }}>
              <div style={styles.reportTypeIcon}>📈</div>
              <h3 style={styles.reportTypeTitle}>Platform Analytics</h3>
              <p style={styles.reportTypeText}>
                Cross-client metrics, completion rates, and trend analysis.
              </p>
              <span style={styles.reportTypeAction}>Coming Soon</span>
            </div>
          </div>
        </div>

        {/* Jobs with Reports */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Job Reports</h2>
          {jobs.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No jobs found. Load some jobs first to generate reports.</p>
              <button 
                onClick={() => navigate('/admin/jobs')}
                style={styles.primaryButton}
              >
                Load Jobs
              </button>
            </div>
          ) : (
            <div style={styles.jobsTable}>
              <div style={styles.tableHeader}>
                <div style={styles.tableCell}>Job Title</div>
                <div style={styles.tableCellSmall}>Candidates</div>
                <div style={styles.tableCellSmall}>Analyzed</div>
                <div style={styles.tableCellSmall}>Top Score</div>
                <div style={styles.tableCellAction}>Action</div>
              </div>
              {jobs.map(job => (
                <div 
                  key={job.id} 
                  style={styles.tableRow}
                  onClick={() => navigate(`/admin/jobs/${job.id}/report`)}
                >
                  <div style={styles.tableCell}>
                    <div style={styles.jobTitle}>{job.title}</div>
                    {job.company_name && (
                      <div style={styles.jobCompany}>{job.company_name}</div>
                    )}
                  </div>
                  <div style={styles.tableCellSmall}>
                    <span style={styles.badge}>{job.interview_count || 0}</span>
                  </div>
                  <div style={styles.tableCellSmall}>
                    <span style={styles.badge}>{job.analyzed_count || 0}</span>
                  </div>
                  <div style={styles.tableCellSmall}>
                    {job.top_score ? (
                      <span style={{
                        ...styles.scoreBadge,
                        background: job.top_score >= 7 ? 'rgba(34, 197, 94, 0.2)' : 
                                   job.top_score >= 5 ? 'rgba(59, 130, 246, 0.2)' : 'rgba(234, 179, 8, 0.2)',
                        color: job.top_score >= 7 ? '#22c55e' : 
                               job.top_score >= 5 ? '#3b82f6' : '#eab308'
                      }}>
                        {job.top_score.toFixed(1)}
                      </span>
                    ) : (
                      <span style={styles.badgeMuted}>—</span>
                    )}
                  </div>
                  <div style={styles.tableCellAction}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/jobs/${job.id}/report`);
                      }}
                      style={styles.viewButton}
                    >
                      View Report →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Platform Overview</h2>
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{summary.total_jobs || 0}</div>
              <div style={styles.statLabel}>Total Jobs</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{summary.total_interviews || 0}</div>
              <div style={styles.statLabel}>Total Interviews</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{summary.total_analyzed || 0}</div>
              <div style={styles.statLabel}>Analyzed</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>
                {summary.average_score ? summary.average_score.toFixed(1) : '—'}
              </div>
              <div style={styles.statLabel}>Avg Score</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)',
    color: '#fff',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    color: '#9ca3af'
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: '#a855f7',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: 40
  },
  retryButton: {
    marginTop: 16,
    padding: '10px 20px',
    background: '#a855f7',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    cursor: 'pointer'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 40px',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  logoImage: {
    height: 48,
    width: 'auto',
    objectFit: 'contain'
  },
  subtitle: {
    fontSize: 14,
    color: '#9ca3af'
  },
  backButton: {
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer'
  },
  content: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '32px 40px'
  },
  section: {
    marginBottom: 48
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 20
  },
  reportTypesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 24
  },
  reportTypeCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12,
    padding: 24
  },
  reportTypeIcon: {
    fontSize: 32,
    marginBottom: 12
  },
  reportTypeTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 8,
    color: '#fff'
  },
  reportTypeText: {
    fontSize: 14,
    color: '#9ca3af',
    lineHeight: 1.5,
    marginBottom: 12
  },
  reportTypeAction: {
    fontSize: 13,
    color: '#a855f7'
  },
  jobsTable: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12,
    overflow: 'hidden'
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
    padding: '16px 20px',
    background: 'rgba(255,255,255,0.03)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    fontSize: 12,
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  tableCell: {},
  tableCellSmall: {
    textAlign: 'center'
  },
  tableCellAction: {
    textAlign: 'right'
  },
  jobTitle: {
    fontWeight: 500,
    color: '#fff'
  },
  jobCompany: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 2
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 12,
    fontSize: 14
  },
  badgeMuted: {
    color: '#6b7280'
  },
  scoreBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600
  },
  viewButton: {
    background: 'rgba(168, 85, 247, 0.2)',
    border: '1px solid #a855f7',
    color: '#a855f7',
    padding: '8px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500
  },
  emptyState: {
    textAlign: 'center',
    padding: 48,
    background: 'rgba(255,255,255,0.02)',
    borderRadius: 12,
    color: '#9ca3af'
  },
  primaryButton: {
    marginTop: 16,
    background: '#a855f7',
    color: '#fff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 16
  },
  statCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    padding: 20,
    textAlign: 'center'
  },
  statNumber: {
    fontSize: 32,
    fontWeight: 700,
    color: '#fff'
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4
  }
};
